import { z } from "zod";

export const GeoLocation = z.object({
  city: z.string(),
  region: z.string(),
  country: z.string(),
});

export type GeoLocationDto = z.infer<typeof GeoLocation>;

export const AudienceTypeList = ["common", "custom", "advanced"] as const;
export type AudienceType = (typeof AudienceTypeList)[number];

export const AudienceFilterList = [
  "equals",
  "contains",
  "startsWith",
  "endsWith",
  "doesNotEqual",
  "doesNotContain",
  "doesNotStartWith",
  "doesNotEndWith",
  "isNull",
  "isNotNull",
  "regex",
] as const;

export type AudienceFilter = (typeof AudienceFilterList)[number];

export const AudienceFilterTypeList = [
  "utm",
  "url",
  "device",
  "visitor",
  "jsExpression",
  "trafficSource",
  "country",
  "referrer",
  "cookie",
  "klaviyo",
  "landingPage",
] as const;
export type AudienceFilterType = (typeof AudienceFilterTypeList)[number];

export const ExpressionTypeList = ["common", "custom"] as const;

export type ExpressionType = (typeof ExpressionTypeList)[number];

export const ExpressionNodeOperator = z.enum([
  "and",
  "or",
  "start_paren",
  "end_paren",
]);

export const ExpressionNodeQuery = z.object({
  key: z.string(),
  value: z.string(),
  filter: z.enum(AudienceFilterList).nullish(),
  type: z.enum(AudienceFilterTypeList).default("utm"),
});

export type ExpressionNodeQueryType = z.infer<typeof ExpressionNodeQuery>;

export const ExpressionNode = z
  .object({
    query: ExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  );

export type ExpressionNodeType = z.infer<typeof ExpressionNode>;

export const AudienceActionList = [
  "experienceInclude",
  "experienceExclude",
  "experienceUnassigned",
  "assignVariation",
  "randomVariation",
  "leaveUnassigned",
] as const;
export type AudienceAction = (typeof AudienceActionList)[number];

export const AudienceExcludeCurrencyDtoSchema = z.object({
  exclude: z.boolean(),
  currency: z.string().nullable(),
  country: z.string().optional(),
  region: z.string().optional(),
  city: z.string().optional(),
});
export type AudienceExcludeCurrencyDto = z.infer<
  typeof AudienceExcludeCurrencyDtoSchema
>;

export const AudienceFiltersDtoSchema = z.object({
  id: z.string().uuid(),
  priority: z.number().int(),
  action: z.enum(AudienceActionList).nullable(),
  filterType: z.enum(AudienceFilterTypeList),
  expression: z.array(ExpressionNode).optional().nullable(),
  expressionType: z.enum(ExpressionTypeList).nullish(),
  variationId: z.string().uuid().nullish(),
  excludeFromAnalytics: z.boolean().default(false),
});
export type AudienceFiltersDto = z.infer<typeof AudienceFiltersDtoSchema>;

export const AudienceDtoSchema = z.object({
  id: z.string().uuid(),
  experienceId: z.string().uuid(),
  enabled: z.boolean(),
  excludeCurrency: AudienceExcludeCurrencyDtoSchema,
  wholesale: z.boolean(),
  audienceType: z.enum(AudienceTypeList).optional(),
  elseAction: z.enum(AudienceActionList).nullable(),
  elseVariationId: z.string().uuid().nullish(),
  elseExcludeFromAnalytics: z.boolean().default(false),
  filters: AudienceFiltersDtoSchema.array().optional().default([]),
});
export type AudienceDto = z.infer<typeof AudienceDtoSchema>;

export const CreateAudienceDtoSchema = z.object({
  id: z.string().uuid(),
  enabled: z.boolean(),
  excludeCurrency: AudienceExcludeCurrencyDtoSchema,
  wholesale: z.boolean(),
  elseAction: z.enum(AudienceActionList).nullable(),
  elseVariationId: z.string().uuid().nullable(),
  experienceId: z.string().uuid().optional(),
  audienceType: z.enum(AudienceTypeList).optional(),
  elseExcludeFromAnalytics: z.boolean(),
  filters: AudienceFiltersDtoSchema.array().optional().default([]),
});
export type CreateAudienceDto = z.infer<typeof CreateAudienceDtoSchema>;

export const EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  assignVariation: {
    title: "Assign to specific test group",
    description: "Visitor will be assigned to a specific test group.",
  },
  randomVariation: {
    title: "Assign randomly",
    description:
      "Visitor will be assigned to a test group randomly based on the %’s defined.",
  },
  experienceExclude: {
    title: "Exclude from test",
    description:
      "Visitor will be excluded from this test for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the test but may be subsequently included.",
  },
};

export const CAMPAIGN_EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  experienceInclude: {
    title: "Include in Campaign",
    description: "Visitor will be included in this campaign.",
  },
  experienceExclude: {
    title: "Exclude from campaign",
    description:
      "Visitor will be excluded from this campaign for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the campaign but may be subsequently included.",
  },
};
